import React, { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import {
  Text,
  Textarea,
  Box,
  Input,
  HStack,
  useToast,
  NumberInput,
  NumberInputField,
  Button,
} from '@chakra-ui/react';
import { IoEnterOutline } from 'react-icons/io5';
import { useForm, Controller } from 'react-hook-form';

import CustomDrawer from '~/components/UI/Drawer';

import { editItem } from '~/firebase/store';

import { selectedStoreIdAtom } from '~/recoil/atom';
import { Item, ItemWithId } from '~/types/store';
import { EditItem } from './types';

import { ButtonVariants } from '~/customTheme/components/Button';

type EditItemProps = {
  isOpen: boolean;
  onClose: () => void;
  item: ItemWithId;
};

const EditItemDrawer: React.FC<EditItemProps> = ({ isOpen, onClose, item }) => {
  const toast = useToast();

  const defaultFormValue = {
    name: item.name,
    description: item.description,
    price: item.price.toString(),
    image: item.image,
  };
  const { register, control, handleSubmit, reset } = useForm<EditItem>({
    defaultValues: defaultFormValue,
  });
  const storeId = useRecoilValue(selectedStoreIdAtom);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      image: item.image,
    });
  }, [isOpen]);

  const validateNoSpace = (name: string) => {
    return name.trim().length !== 0;
  };

  const validateNoPoint = (name: string) => {
    return !name.includes('.');
  };

  const handleSaveNewItem = async (data: EditItem) => {
    if (!storeId) return;
    const { id: itemId, ...restOfItem } = item;
    const newItem: Item = {
      ...restOfItem,
      ...data,
      name: data.name.trim(),
      price: parseInt(data.price),
      image: data.image?.trim() || '',
    };
    try {
      await editItem({ storeId, itemId, newItemObject: newItem });
      toast({
        position: 'top',
        title: '修改成功',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        position: 'top',
        title: '發生錯誤',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleOpenImageUrl = () => {
    window.open('https://weserve-gm.sanity.studio/desk/mallImage');
  };

  return (
    <CustomDrawer
      title="品項詳細頁"
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSubmit(handleSaveNewItem)}
    >
      <Box as="label" justifyContent="space-between">
        <Text mb="0.2rem" htmlFor="name" color="#8F8F8F" fontSize="md">
          品項名稱
        </Text>
        <Controller
          control={control}
          name="name"
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              isInvalid={!!error}
              errorBorderColor="admin.error"
              focusBorderColor={!!error ? 'admin.error' : 'admin.black.700'}
            />
          )}
          rules={{ required: true, validate: (name) => validateNoSpace(name) }}
        />
      </Box>

      <Box as="label" justifyContent="space-between">
        <Text mb="0.2rem" htmlFor="price" color="#8F8F8F" fontSize="md">
          品項價錢
        </Text>
        <Controller
          control={control}
          name="price"
          render={({ field, fieldState: { error } }) => (
            <NumberInput
              {...field}
              isInvalid={!!error}
              errorBorderColor="admin.error"
              focusBorderColor={!!error ? 'admin.error' : 'admin.black.700'}
            >
              <NumberInputField />
            </NumberInput>
          )}
          rules={{ required: true, min: 0, validate: (price) => validateNoPoint(price) }}
        />
      </Box>

      <Box as="label" justifyContent="space-between">
        <Text mb="0.2rem" htmlFor="description" color="#8F8F8F" fontSize="md">
          品項敘述
        </Text>
        <Textarea {...register('description')} />
      </Box>

      <Box as="label" justifyContent="space-between">
        <Text mb="0.2rem" htmlFor="image" color="#8F8F8F" fontSize="md">
          品項圖片連結
        </Text>
        <Controller
          control={control}
          name="image"
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <Input {...field} isInvalid={!!error} errorBorderColor="admin.error" />
          )}
        />
      </Box>
      <HStack justify="flex-end">
        <Button
          variant={ButtonVariants.MODAL_PRIMARY}
          fontSize="sm"
          fontWeight="normal"
          w="8rem"
          rightIcon={<IoEnterOutline />}
          onClick={handleOpenImageUrl}
        >
          圖片上傳連結
        </Button>
      </HStack>
    </CustomDrawer>
  );
};

export default EditItemDrawer;
